import { DSLink, DSText } from '@engenharia/ds-react';
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdMailOutline, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { BiLeftArrowAlt } from 'react-icons/bi';

import MetaHeader from '../../components/MetaHeader';
import Captcha from '../../components/Captcha';
import Button from '../../components/atoms/Button';
import CircularLoader from '../../components/CircularLoader';
import Header from '../../components/Header';
import Notification from '../../components/Notification';
import SelectCountry from '../../components/SelectCountry';

import { EmailSuggest, HeaderContent } from './styles';

import {
  BackBtn,
  CallToActionBox,
  Card,
  ChoiceGroup,
  Container,
  ContainerBanner,
  EmailPasswordChoiceBtn,
  EmailPasswordBtnContent,
  EmailPasswordBtnPrefix,
  EmailPasswordBtnHighlight,
  EmailPasswordBtnIcon,
  Image,
  Section,
  SignInFreeButton,
  SignupBox,
} from '../SignIn/styles';

import BlackFriday from '../../components/Banner/BlackFriday';
import FormHelperTextValidation from '../../components/FormHelperValidation';
import { Keys } from '../../constants';
import api from '../../services/api';
import { getLanguage, setAccountType, logout } from '../../services/auth';
import authApi from '../../services/authApi';
import { setDataLayer } from '../../services/gtm';
import { redirectUser } from '../../services/user';
import { allCountries } from '../../utils/countries';
import { emailSuggestion } from '../../utils/email';
import { kebabize } from '../../utils/kebabize';
import { transcodeLanguage } from '../../utils/language';
import { removeFromLocalStorage, saveToLocalStorage } from '../../utils/localStorage';
import { getUrlParam } from '../../utils/params';
import { getMarketingParams, persistMarketingParams } from '../../utils/marketingParams';
import { sanitizeEmailAddress } from '../../utils/sanitize';
import {
  mailValidation,
  requiredValitidation,
  strongPasswordValidation,
} from '../../utils/validations';
import { countryCode, defaultCountryCode } from '../../config/i18n';
import useCore from '../../hooks/useCore';
import { SocialIconButtons } from '../../components/molecules/SocialIconButton';
import Logo from '../../components/molecules/Logo';
import { Hidden } from '../../components/utilities/Hidden';
import { isReactNativeWebView } from '../../utils/isReactNativeWebView';

const PRODUCT_KEY = process.env.REACT_APP_PRODUCT_KEY;

export default function Signup() {
  const { t, i18n } = useTranslation();
  const { validateCoupon } = useCore();
  const history = useHistory();
  const RefCaptchaSignup = useRef(null);
  const RefCaptchaSignin = useRef(null);

  const params = new URLSearchParams(window.location.search);

  const initialLanguage = getLanguage();
  const [countriesList, setCountriesList] = useState(allCountries(initialLanguage));

  const handleCouponStorage = (value) => {
    const coupon = value?.trim();
    if (coupon) {
      saveToLocalStorage(Keys.COUPON_CODE, coupon);
    } else {
      removeFromLocalStorage(Keys.COUPON_CODE);
    }
  };

  const [state, setState] = useState(() => {
    const initialState = {
      email: '',
      emailValidation: {},
      name: '',
      nameValidation: false,
      country: defaultCountryCode,
      countryCode: defaultCountryCode,
      password: '',
      signUpPasswordValidation: {},
      showPassword: false,
    };

    const name = params.get('name');
    const email = params.get('email');

    const country = countryCode[getLanguage()] || initialState.country;

    return {
      ...initialState,
      name: name || initialState.name,
      email: email || initialState.email,
      countryCode: country,
      country,
    };
  });

  const [showForm, setShowForm] = useState(false);
  const [notification, setNotification] = useState({});
  const [showCouponField, setShowCouponField] = useState(false);
  const [couponParams, setCouponParams] = useState('');
  const [couponValidation, setCouponValidation] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const accountOrigin = getUrlParam('origin') || '';

  const marketingParams = getMarketingParams();

  const handleNameChange = (event) => {
    setState({
      ...state,
      name: event.target.value,
      nameValidation: requiredValitidation(event.target.value),
    });
  };

  const handleEmailChange = (event) => {
    const email = sanitizeEmailAddress(event.target.value);
    const suggestion = emailSuggestion(email);

    setState({
      ...state,
      email,
      emailValidation: { ...mailValidation(email), suggestion },
    });
  };

  const handleCountryNameChange = (currentCountryIso2) => {
    setState({
      ...state,
      country: currentCountryIso2,
      countryCode: currentCountryIso2,
    });
  };

  const validateCouponDebounced = useCallback(
    debounce(async (value) => {
      const couponCode = value?.trim();
      if (!couponCode) {
        setCouponValidation({});
        setIsValidatingCoupon(false);
        return;
      }

      setIsValidatingCoupon(true);

      const couponValidationData = {
        isValid: null,
        message: '',
      };

      try {
        await validateCoupon(couponCode);
        couponValidationData.isValid = true;
      } catch (error) {
        setShowCouponField(true);
        couponValidationData.isValid = false;
        couponValidationData.message = error?.error || error?.message || t('errors.coupon-invalid');
      } finally {
        setIsValidatingCoupon(false);
        setCouponValidation(couponValidationData);
      }
    }, 700),
    [],
  );

  const handleCouponChange = (value) => {
    setCouponParams(value);
    setIsValidatingCoupon(true);
    validateCouponDebounced(value);
  };

  const handlePasswordChange = (event) => {
    const newValue = event.target.value.replace(' ', '');

    setState({
      ...state,
      password: newValue,
      signUpPasswordValidation: strongPasswordValidation(newValue),
    });
  };

  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    const validationFields = {
      emailInvalidError: state.emailValidation?.emailInvalidError,
      emailUnconfirmedError: state.emailValidation?.emailUnconfirmedError,
      strongPasswordInvalidError: state.signUpPasswordValidation?.passwordInvalidError,
      couponInvalidError: couponParams && couponValidation?.isValid === false,
    };

    const someError = Object.keys(validationFields).find((error) => validationFields[error]);

    if (someError) {
      const translationKey = kebabize(someError)?.replace('-error', '');

      setNotification({
        key: new Date().getTime(),
        message: t(`errors.${translationKey}`),
      });

      return;
    }

    const { email, name, password, country } = state;
    const language = transcodeLanguage(getLanguage());
    const captcha = await RefCaptchaSignup?.current?.executeAsync();
    const captchaLogin = await RefCaptchaSignin?.current?.executeAsync();

    setIsLoading(true);

    try {
      if (couponParams) handleCouponStorage(couponParams);

      await api.post('/accounts', {
        email,
        name,
        password,
        language,
        captcha,
        country,
        coupon_code: couponParams,
        product_key: PRODUCT_KEY,
        origin: accountOrigin,
        ...marketingParams,
      });

      logout();

      const responseLogin = await authApi.post('/accounts/sign_in', {
        email,
        password,
        callback_url: process.env.REACT_APP_FULL_URL,
        language,
        captcha: captchaLogin,
      });

      await redirectUser(responseLogin, true);
    } catch (error) {
      RefCaptchaSignup?.current?.reset();

      setIsLoading(false);

      const { response } = error;

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  useEffect(() => {
    persistMarketingParams();

    const coupon = params.get('coupon') || params.get('COUPON');

    setDataLayer({
      event: 'begin_register',
    });

    handleCouponStorage(coupon);
    if (coupon) handleCouponChange(coupon);

    return () => {
      if (validateCouponDebounced) validateCouponDebounced.cancel();
    };
  }, []);

  useEffect(() => {
    if (i18n.language) {
      const languageMap = {
        en: 'en',
        es: 'es',
        pt: 'pt',
      };

      const languageCode = languageMap[i18n.language] || languageMap.pt;

      setCountriesList(allCountries(languageCode));
      handleCountryNameChange(countryCode[languageCode] || defaultCountryCode);
    }
  }, [i18n.language]);

  const handleFacebookLogin = async (data) => {
    const { profile, tokenDetail } = data;
    const facebookUserId = tokenDetail?.userID ?? (profile?.id != null ? String(profile.id) : '');
    const captcha = await RefCaptchaSignup?.current?.executeAsync();
    const language = transcodeLanguage(getLanguage());

    try {
      const response = await authApi.post('/provider_sign_in', {
        captcha,
        language,
        provider: 'facebook',
        user_id: facebookUserId,
        token: tokenDetail.accessToken,
        name: profile.name,
        email: profile.email,
        callback_url: process.env.REACT_APP_FULL_URL,
        ...marketingParams,
      });

      setAccountType('facebook');

      await redirectUser(response, true);
    } catch (error) {
      const { response } = error;

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  const handleMicrosoftLogin = async (data) => {
    if (data?.error || !data?.code) {
      setNotification({
        key: new Date().getTime(),
        message: data?.error || t('errors.connection-failure'),
      });
      return;
    }
    try {
      const captcha = await RefCaptchaSignup?.current?.executeAsync();
      const response = await authApi.post('/provider_sign_in', {
        provider: 'microsoft',
        user_id: 'microsoft_code_exchange',
        token: data.code,
        redirect_uri: data.redirectUri,
        captcha,
        callback_url: process.env.REACT_APP_FULL_URL,
        language: transcodeLanguage(getLanguage()),
        ...marketingParams,
      });
      setAccountType('microsoft');
      await redirectUser(response, true);
    } catch (error) {
      const { response } = error;
      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  const handleAppleLogin = async (data) => {
    if (data?.error || !data?.token || !data?.user) {
      setNotification({
        key: new Date().getTime(),
        message: data?.error || t('errors.connection-failure'),
      });
      return;
    }
    try {
      const captcha = await RefCaptchaSignup?.current?.executeAsync();
      const response = await authApi.post('/provider_sign_in', {
        provider: 'apple',
        user_id: data.user.sub,
        token: data.token,
        name: data.user.name,
        email: data.user.email,
        captcha,
        callback_url: process.env.REACT_APP_FULL_URL,
        language: transcodeLanguage(getLanguage()),
        ...marketingParams,
      });
      setAccountType('apple');
      await redirectUser(response, true);
    } catch (error) {
      const { response } = error;
      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  const handleSignInRedirect = (event) => {
    event.preventDefault();
    history.push('/accounts/sign_in');
  };

  const inWebView = isReactNativeWebView();

  const logo = <Logo fullWidth imgComponent={Image} />;

  const signInBox = (
    <SignupBox>
      <DSText variant="subtitle2" color="textSecondary">
        {t('signup.caption.subtitle')}
      </DSText>
      <SignInFreeButton onClick={handleSignInRedirect} variant="outline" color="orange">
        {t('signup.caption.action')}
      </SignInFreeButton>
    </SignupBox>
  );

  return (
    <>
      {!inWebView && <Header positionFixed />}
      <Container>
        <MetaHeader
          title="Cadastro para Teste Grátis - mLabs | Gestão de redes sociais"
          description="Cadastre-se e Teste grátis! Cadastro sem cartão de crédito. Gestão de Redes Sociais é com a mLabs."
        />
        {!inWebView && (
          <ContainerBanner>
            <BlackFriday />
          </ContainerBanner>
        )}
        <Section>
          <CallToActionBox device="laptop">
            <Box marginBottom={16}>{logo}</Box>
            {signInBox}
          </CallToActionBox>
          <Card>
            <Hidden invert marginBottom={4} device="laptop">
              {logo}
            </Hidden>

            {/* Choice menu */}
            {!showForm && (
              <ChoiceGroup>
                <HeaderContent>
                  <DSText
                    component="h1"
                    variant="h4"
                    style={{
                      whiteSpace: 'pre-line',
                      color: '#1f1f1f',
                      fontSize: '20px',
                      fontWeight: '500',
                    }}
                  >
                    {t('signup.title')}
                  </DSText>
                  <DSText variant="caption" weight={600} style={{ color: '#616161' }}>
                    {t('signup.info.card')} {t('signup.info.spam')}
                  </DSText>
                </HeaderContent>
                <SocialIconButtons
                  onFacebookCompleted={handleFacebookLogin}
                  onMicrosoftCompleted={handleMicrosoftLogin}
                  onAppleCompleted={handleAppleLogin}
                  prefix={t('signup.choice.socialPrefix')}
                />
                <EmailPasswordChoiceBtn type="button" onClick={() => setShowForm(true)}>
                  <EmailPasswordBtnContent>
                    <EmailPasswordBtnPrefix>
                      {t('signup.choice.email-password')}
                    </EmailPasswordBtnPrefix>
                    <EmailPasswordBtnHighlight>
                      {t('signup.choice.email-password-highlight')}
                    </EmailPasswordBtnHighlight>
                  </EmailPasswordBtnContent>
                  <EmailPasswordBtnIcon>
                    <MdMailOutline />
                  </EmailPasswordBtnIcon>
                </EmailPasswordChoiceBtn>
              </ChoiceGroup>
            )}

            {/* Full registration form */}
            {showForm && (
              <form onSubmit={handleSubmit} data-testid="form" style={{ width: '100%' }}>
                <BackBtn type="button" onClick={() => setShowForm(false)}>
                  <BiLeftArrowAlt /> {t('button.back')}
                </BackBtn>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    required
                    type="text"
                    id="name"
                    name="name"
                    data-testid="formName"
                    label={t('signup.form.name')}
                    value={state.name}
                    onChange={handleNameChange}
                    variant="outlined"
                    autoComplete="name"
                    autoFocus
                    error={state.nameValidation}
                    inputProps={{ maxLength: 100 }}
                    style={{ marginBottom: 0 }}
                  />
                  {state.nameValidation && (
                    <FormHelperText error id="name-error">
                      {t('errors.name-invalid')}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    required
                    type="email"
                    id="email"
                    name="email"
                    data-testid="formEmail"
                    label={t('signup.form.email')}
                    value={state.email}
                    onChange={handleEmailChange}
                    variant="outlined"
                    autoComplete="email"
                    error={
                      state.emailValidation?.emailInvalidError ||
                      state.emailValidation?.emailUnconfirmedError
                    }
                    style={{ marginBottom: 0 }}
                  />
                  {state.emailValidation?.emailInvalidError && (
                    <FormHelperText error id="email-error">
                      {t('errors.email-invalid')}
                    </FormHelperText>
                  )}
                  {state.emailValidation?.emailUnconfirmedError && (
                    <FormHelperText error id="email-error">
                      {t('errors.email-unconfirmed')}
                    </FormHelperText>
                  )}
                  {state.emailValidation?.suggestion && (
                    <EmailSuggest variant="caption">
                      {t('errors.email-suggestion')}{' '}
                      <strong>{state.emailValidation?.suggestion}</strong>?
                    </EmailSuggest>
                  )}
                </FormControl>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    label={`${t('signup.form.password')}`}
                    variant="outlined"
                    required
                    id="password"
                    name="password"
                    data-testid="formPassword"
                    type={state.showPassword ? 'text' : 'password'}
                    value={state.password}
                    onChange={handlePasswordChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment>
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {state.showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={state.signUpPasswordValidation?.passwordInvalidError}
                    style={{ marginBottom: 0 }}
                  />
                  {state.password && (
                    <FormHelperTextValidation validation={state.signUpPasswordValidation} />
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <SelectCountry
                    fullWidth
                    selected={state.country}
                    onSelect={handleCountryNameChange}
                    allCountriesWithCustomLabel={countriesList}
                    searchable
                    searchPlaceholder=" "
                  />
                </FormControl>

                {showCouponField ? (
                  <FormControl variant="outlined" fullWidth>
                    <TextField
                      label={`${t('signup.form.coupon')}`}
                      id="coupon"
                      name="coupon"
                      data-testid="formCoupon"
                      type="text"
                      value={couponParams}
                      onChange={(event) => handleCouponChange(event?.target?.value)}
                      variant="outlined"
                      margin="normal"
                      error={couponValidation.isValid === false}
                      helperText={
                        isValidatingCoupon
                          ? t('signup.form.couponValidating')
                          : couponValidation.message || ''
                      }
                      InputProps={{
                        maxLength: 100,
                        endAdornment:
                          (isValidatingCoupon && (
                            <InputAdornment>
                              <CircularLoader scale="0.4" color="var(--color-gray-500)" />
                            </InputAdornment>
                          )) ||
                          null,
                      }}
                    />
                  </FormControl>
                ) : (
                  <FormControl fullWidth style={{ textAlign: 'center', marginTop: 4 }}>
                    <DSLink variant="subtitle2" onClick={() => setShowCouponField((v) => !v)}>
                      {t('signup.caption.coupon')}
                    </DSLink>
                  </FormControl>
                )}
                <FormControl fullWidth style={{ marginTop: 12 }}>
                  <Button
                    type="submit"
                    id="btnSubmit"
                    data-testid="formSubmit"
                    disabled={isLoading}
                    endIcon={isLoading && <CircularLoader color="black" scale="0.4" />}
                  >
                    {!isLoading && t('signup.button.start')}
                  </Button>
                </FormControl>
              </form>
            )}

            <DSText variant="caption" weight={400} style={{ marginTop: 12 }}>
              {t('signup.caption.title')}
              <br />
              <DSLink
                variant="caption"
                target="_blank"
                rel="noreferrer"
                href="https://www.mlabs.com.br/politica-de-privacidade"
              >
                {t('signup.caption.privacy')}
              </DSLink>
              {t('signup.caption.and')}
              <DSLink
                variant="caption"
                target="_blank"
                rel="noreferrer"
                href="https://www.mlabs.com.br/termos-de-uso"
              >
                {t('signup.caption.terms')}
              </DSLink>
              {t('signup.caption.dot')}
            </DSText>

            <Hidden invert device="laptop">
              {signInBox}
            </Hidden>
          </Card>
        </Section>
      </Container>
      {notification?.key && (
        <Notification index={notification.key} message={notification.message} />
      )}
      <Captcha ref={RefCaptchaSignup} />
      <Captcha ref={RefCaptchaSignin} size="invisible" />
    </>
  );
}
